import { ApiNeighborContext } from '../api-types/neighbor.api';
import { ApiCreateRelative, ApiRelative, ApiUpdateRelative } from '../api-types/relative.api';
import { NeighborContext } from '../app-types/neighbors.app';
import { CreateRelative, Relative, UpdateRelative } from '../app-types/relative.app';
import { transformApiAddressToAppAddress, transformAppAddressToApiAddress } from './address.transform';
import {
	transformApiContactPointsToAppContactPoints,
	transformAppContactPointToApiCreateContactPoint,
	transformAppCreateContactPointsToApiContactPoints,
} from './contact-point.transform';
import { transformApiPersonTypeToAppPersonType } from './person-type.transformer';
import { transformApiUserToAppUser } from './user.transform';

/**
 * Transforms an API relative object to an app relative object.
 * @param input The API relative object
 * @returns The app relative object
 */
export const transformApiRelativeToAppRelative = (input: ApiRelative): Relative => {
	const output = transformApiUserToAppUser(input) as Relative;
	output.lastName = input.name ?? '';
	output.isSupervisor = input.isSupervisor;
	output.remark = input.remark;
	output.otherText = input.otherText;
	output.categoryPersonCategory = {
		id: input.categoryPersonCategory.id,
		name: input.categoryPersonCategory.name,
		isInitiator: input.categoryPersonCategory.isInitiator,
		isPersonal: input.categoryPersonCategory.isPersonal,
	};
	if (input.categoryTypeOfCare)
		output.categoryTypeOfCare = {
			integrationOfficeId: input.categoryTypeOfCare.integrationOfficeId,
			id: input.categoryTypeOfCare.id,
			remark: input.categoryTypeOfCare.remark,
			initial: input.categoryTypeOfCare.initial,
		};
	if (input.address) output.address = transformApiAddressToAppAddress(input.address);
	output.personType = transformApiPersonTypeToAppPersonType(input.personType);
	output.contactPoints = transformApiContactPointsToAppContactPoints(input.contactPoints || []);
	output.hasBeenClarified = input.hasBeenClarified;
	output.clarifiedAt = input.clarifiedAt ? new Date(input.clarifiedAt) : undefined;
	output.typeOfClarification = input.typeOfClarification;

	return output;
};

/**
 * Transforms an API relatives array to an app relatives array.
 * @param input The API relatives array
 * @returns The app relatives array
 */
export const transformApiRelativesToAppRelatives = (input: ApiRelative[]): Relative[] => {
	return input.map(transformApiRelativeToAppRelative);
};

/**
 * Transforms a CreateRelative object to an ApiCreateRelative object.
 * @param input The CreateRelative object
 * @returns The ApiCreateRelative object
 */
export const transformCreateRelativeToApiCreateRelative = (input: CreateRelative): ApiCreateRelative => {
	const output: ApiCreateRelative = {
		firstName: input.firstName,
		name: input.name,
		title: input.title,
		isSupervisor: input.isSupervised,
		genderId: input.genderId,
		remark: input.remark,
		categoryPersonCategoryId: input.personCategoryId,
		hasBeenClarified: input.hasBeenClarified,
		clarifiedAt: input.clarifiedAt?.toISOString(),
		typeOfClarification: input.typeOfClarification,
	};

	if (input.communicationWays)
		output.contactPoints = transformAppCreateContactPointsToApiContactPoints(input.communicationWays);
	if (input.address) output.address = transformAppAddressToApiAddress(input.address);

	return output;
};

/**
 * Transforms an UpdateRelative object to an ApiUpdateRelative object.
 * @param input The UpdateRelative object
 * @returns The ApiUpdateRelative object
 */
export const transformUpdateRelativeToApiUpdateRelative = (input: UpdateRelative): ApiUpdateRelative => {
	const output: ApiUpdateRelative = {
		firstName: input.firstName,
		name: input.lastName,
		title: input.title,
		otherText: input.otherText,
		isSupervisor: input.isSupervisor,
		genderId: input.genderId,
		remark: input.remark,
		personTypeId: input.personTypeId,
		categoryPersonCategoryId: input.categoryPersonCategoryId,
		categoryTypeOfCareId: input.categoryTypeOfCareId,
		hasBeenClarified: input.hasBeenClarified,
		clarifiedAt: input.clarifiedAt?.toISOString(),
		typeOfClarification: input.typeOfClarification,
	};

	if (input.contactPoints)
		output.contactPoints = input.contactPoints.map(transformAppContactPointToApiCreateContactPoint);
	if (input.address) output.address = transformAppAddressToApiAddress(input.address);

	return output;
};

export function transformApiRelativeNeighborsToAppRelativeNeighbors(
	input: ApiNeighborContext<ApiRelative>,
): NeighborContext<Relative> {
	return {
		current: transformApiRelativeToAppRelative(input.current),
		previous: input.previous ? transformApiRelativeToAppRelative(input.previous) : null,
		next: input.next ? transformApiRelativeToAppRelative(input.next) : null,
	};
}
